import chatNexusLogo from '../assets/ChatNexus_Logo.png';
import plotweaveLogo from '../assets/Plotweave_Logo.png';

/**
 * Central brand configuration.
 *
 * The same codebase ships as multiple products, selected at build time via
 * the `REACT_APP_BRAND` env var (CRA inlines it at build). Everything that
 * differs between brands — name, logo, theme, default landing route, bottom
 * nav order, support email — is read from here so the rest of the app stays
 * brand-agnostic.
 *
 * Defaults to `chatnexus` so existing builds are unchanged.
 */

export type BrandId = 'chatnexus' | 'plotweave';

export type NavItemId = 'stories' | 'home' | 'settings';

export interface Brand {
  id: BrandId;
  /** Display name used in copy, titles, and as the i18n {{brandName}} value. */
  name: string;
  /** Imported logo asset, or null to render the name as a text wordmark. */
  logo: string | null;
  /** Primary theme color (matches Tailwind primary / PWA theme_color). */
  themeColor: string;
  /** Where a logged-in user lands (RootRedirect + OAuth success). */
  defaultRoute: string;
  /** Where a brand-new user goes after signup when onboarding is needed. */
  onboardingRoute: string;
  /** Support contact address shown in UI/legal copy. */
  supportEmail: string;
  /** Bottom-nav item order (left → right). Only listed items are shown. */
  navOrder: NavItemId[];
}

const BRANDS: Record<BrandId, Brand> = {
  chatnexus: {
    id: 'chatnexus',
    name: 'ChatNexus',
    logo: chatNexusLogo,
    themeColor: '#a855f7',
    defaultRoute: '/dashboard',
    onboardingRoute: '/onboarding',
    supportEmail: 'support@chatnexus.com',
    navOrder: ['stories', 'home', 'settings'],
  },
  plotweave: {
    id: 'plotweave',
    name: 'Plotweave',
    // Whimsical colored emblem — shown true-color (no invert treatment).
    logo: plotweaveLogo,
    themeColor: '#a855f7',
    // Story-first: land straight in the story library.
    defaultRoute: '/stories',
    onboardingRoute: '/onboarding',
    supportEmail: 'support@plotweave.ai',
    navOrder: ['stories', 'home', 'settings'],
  },
};

const ACTIVE_BRAND_ID = (process.env.REACT_APP_BRAND as BrandId) || 'plotweave';

export const brand: Brand = BRANDS[ACTIVE_BRAND_ID] || BRANDS.chatnexus;

export default brand;
